import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Baby } from '../lib/supabase';
import { useAuth } from './useAuth';

export const useBabies = () => {
  const [babies, setBabies] = useState<Baby[]>([]);
  const [activeBaby, setActiveBaby] = useState<Baby | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchBabies();
    } else {
      setBabies([]);
      setActiveBaby(null);
      setLoading(false);
    }
  }, [user]);

  const fetchBabies = async () => {
    try {
      const { data, error } = await supabase
        .from('babies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setBabies(data || []);
      
      // Set first baby as active if none selected
      if (data && data.length > 0 && !activeBaby) {
        setActiveBaby(data[0]);
      }
    } catch (error) {
      console.error('Error fetching babies:', error);
    } finally {
      setLoading(false);
    }
  };

  const addBaby = async (babyData: Omit<Baby, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('babies')
        .insert([{ ...babyData, user_id: user?.id }])
        .select()
        .single();

      if (error) throw error;

      setBabies(prev => [data, ...prev]);
      if (!activeBaby) {
        setActiveBaby(data);
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Error adding baby:', error);
      return { data: null, error };
    }
  };

  const updateBaby = async (id: string, updates: Partial<Baby>) => {
    try {
      const { data, error } = await supabase
        .from('babies')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setBabies(prev => prev.map(baby => baby.id === id ? data : baby));
      if (activeBaby?.id === id) {
        setActiveBaby(data);
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Error updating baby:', error);
      return { data: null, error };
    }
  };

  const deleteBaby = async (id: string) => {
    try {
      console.log('Iniciando eliminación del bebé:', id);
      
      // Eliminar todos los registros relacionados primero
      const tables = ['feeding_records', 'sleep_records', 'diaper_records', 'growth_records', 'milestones'];
      
      for (const table of tables) {
        console.log(`Eliminando registros de ${table}...`);
        const { error: deleteError } = await supabase
          .from(table)
          .delete()
          .eq('baby_id', id);
        
        if (deleteError) {
          console.warn(`Error deleting ${table}:`, deleteError);
          // Continuar con las otras tablas aunque una falle
        } else {
          console.log(`Registros de ${table} eliminados correctamente`);
        }
      }

      // Luego eliminar el bebé
      console.log('Eliminando bebé...');
      const { error } = await supabase
        .from('babies')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error eliminando bebé:', error);
        throw error;
      }

      console.log('Bebé eliminado correctamente');

      // Actualizar el estado local
      setBabies(prev => {
        const newBabies = prev.filter(baby => baby.id !== id);
        console.log('Bebés restantes:', newBabies.length);
        
        // Si el bebé eliminado era el activo, seleccionar otro
        if (activeBaby?.id === id) {
          const newActiveBaby = newBabies.length > 0 ? newBabies[0] : null;
          console.log('Nuevo bebé activo:', newActiveBaby?.name || 'ninguno');
          setActiveBaby(newActiveBaby);
        }
        
        return newBabies;
      });
      
      console.log('Eliminación completada exitosamente');
      return { error: null };
    } catch (error) {
      console.error('Error deleting baby:', error);
      alert('Error al eliminar el bebé. Por favor, intenta de nuevo.');
      return { error };
    }
  };

  return {
    babies,
    activeBaby,
    selectedBaby: activeBaby, // Alias para compatibilidad
    setActiveBaby,
    loading,
    addBaby,
    updateBaby,
    deleteBaby,
    refetch: fetchBabies,
  };
};
